import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ArmState } from '../api'
import type { Site } from '../types'
import { armsAgain, armTitle } from '../armTime'

// ArmSwitch shows whether a site is armed and flips it. Disarmed is amber: the
// site's ordinary activity raises no card, which a dispatcher should see.
// Disarming asks first — it was one click on a small label beside the collapse
// arrow, silently stopping every card for the site (#236); arming does not.
export function ArmSwitch({ site, state, busy, onSetArmed }: { site: Site; state: ArmState; busy: boolean; onSetArmed: (siteId: string, armed: boolean) => void }) {
  const [asking, setAsking] = useState(false)
  const name = site.name || site.id
  return (
    <>
      {/* aria-disabled while a change runs rather than disabled, which drops
          focus: confirming a disarm left it on the page (#251). */}
      <button
        role="switch"
        aria-disabled={busy || undefined}
        aria-checked={state.armed}
        onClick={() => {
          if (busy) return
          if (state.armed) setAsking(true)
          else onSetArmed(site.id, true)
        }}
        className={`shrink-0 h-8 max-sm:h-11 px-3 flex items-center gap-2 rounded-full border text-sm font-semibold transition-colors ${
          state.armed
            ? 'border-zinc-800 bg-zinc-900 text-live hover:bg-zinc-800'
            : 'border-attention/40 bg-attention/10 text-attention hover:bg-attention/15'
        } aria-disabled:opacity-50 aria-disabled:cursor-wait`}
        title={armTitle(site, state)}
      >
        {/* Solid when armed, hollow when not, so the two differ by more than colour. */}
        <span aria-hidden="true" className={`w-2 h-2 rounded-full ${state.armed ? 'bg-current' : 'border-2 border-current'}`} />
        {/* Named by the site and what it shows, so saying "Disarmed" reaches
            it; "Site A armed" whatever it read did not (WCAG 2.5.3). */}
        <span className="sr-only">{name}</span>{' '}
        {state.armed ? 'Armed' : 'Disarmed'}
      </button>
      {asking && (
        <DisarmDialog
          name={name}
          armsAgain={armsAgain(state)}
          onCancel={() => setAsking(false)}
          onConfirm={() => {
            setAsking(false)
            onSetArmed(site.id, false)
          }}
        />
      )}
    </>
  )
}

// DisarmDialog is drawn on <body> rather than where the switch is, so no
// ancestor's transform or overflow can clip it and leave the page behind
// clickable (#239). While open, everything else is inert.
function DisarmDialog({ name, armsAgain, onCancel, onConfirm }: { name: string; armsAgain: string; onCancel: () => void; onConfirm: () => void }) {
  const cancel = useRef<HTMLButtonElement>(null)
  const confirm = useRef<HTMLButtonElement>(null)
  const [host] = useState(() => document.createElement('div'))
  useEffect(() => {
    // Focus goes back to where it was — the switch — when the dialog closes.
    const opener = document.activeElement as HTMLElement | null
    document.body.appendChild(host)
    const behind = Array.from(document.body.children).filter((el) => el !== host && !el.hasAttribute('inert'))
    for (const el of behind) el.setAttribute('inert', '')
    cancel.current?.focus()
    return () => {
      for (const el of behind) el.removeAttribute('inert')
      host.remove()
      opener?.focus()
    }
  }, [host])
  // Tab stays on the dialog's two buttons, as aria-modal says it should (#248).
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') return onCancel()
    if (e.key !== 'Tab') return
    e.preventDefault()
    const next = document.activeElement === cancel.current ? confirm.current : cancel.current
    next?.focus()
  }
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="disarm-title"
        aria-describedby="disarm-what"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
        className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-900 p-[22px] flex flex-col gap-3.5 normal-case tracking-normal shadow-[0_24px_48px_rgb(0_0_0/0.6)]"
      >
        <div className="flex items-start gap-3.5">
          <span aria-hidden="true" className="shrink-0 w-10 h-10 rounded-full border border-attention/55 bg-attention/10 text-attention flex items-center justify-center">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10zM4 4l16 16" /></svg>
          </span>
          <div className="flex flex-col gap-1.5">
            <h2 id="disarm-title" className="text-base font-semibold text-zinc-100">Disarm {name}?</h2>
            <p id="disarm-what" className="text-sm text-zinc-400 leading-normal">
              Detections at {name} are still recorded, but raise no alert until it’s armed again.
            </p>
          </div>
        </div>
        <ul className="sm:ml-[54px] flex flex-col gap-2 text-sm text-zinc-200">
          <li className="flex gap-2">
            <svg aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2" /></svg>
            {armsAgain}
          </li>
          <li className="flex gap-2">
            <svg aria-hidden="true" className="w-4 h-4 mt-0.5 shrink-0 text-attention" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
            Tampering is still reported.
          </li>
        </ul>
        <div className="mt-1 flex justify-end gap-2.5">
          <button ref={cancel} onClick={onCancel} className="h-10 px-4 rounded-md border border-zinc-700 bg-zinc-800 text-sm font-semibold text-zinc-100 hover:bg-zinc-700">Cancel</button>
          <button ref={confirm} onClick={onConfirm} className="h-10 px-4 rounded-md text-sm font-bold bg-attention text-zinc-950 hover:brightness-110">Disarm {name}</button>
        </div>
      </div>
    </div>,
    host,
  )
}
